import { Button, Empty, Modal, Table, Typography } from 'antd';
import React, { useState } from 'react';
import styled from 'styled-components';
import { DatasetProfile } from '../../../../../../../../types.generated';
import ColumnStats from '../../snapshot/ColumnStats';
import TableStats from '../../snapshot/TableStats';
import { formatBytes, formatNumberWithoutAbbreviation } from '../../../../../../../shared/formatNumber';
import { useTranslation } from '../../../../../../../../i18n/useTranslation';

export const ChartTable = styled(Table)`
    margin-top: 16px;
`;

export type Props = {
    profiles: Array<DatasetProfile>;
};

const bytesFormatter = (bytes: number) => {
    const formattedBytes = formatBytes(bytes);
    const fullBytes = formatNumberWithoutAbbreviation(bytes);
    return `${formattedBytes.number} ${formattedBytes.unit} (${fullBytes} bytes)`;
};

export default function ProfilingRunsChart({ profiles }: Props) {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [selectedProfileIndex, setSelectedProfileIndex] = useState(-1);

    const showProfileModal = (index: number) => {
        setSelectedProfileIndex(index);
        setShowModal(true);
    };

    const onClose = () => {
        setShowModal(false);
        setSelectedProfileIndex(-1);
    };

    const tableData = profiles.map((profile) => {
        const profileDate = new Date(profile.timestampMillis);
        return {
            timestamp: `${profileDate.toLocaleDateString()} ${t('at')} ${profileDate.toLocaleTimeString()}`,
            rowCount: profile.rowCount?.toString() || '-',
            columnCount: profile.columnCount?.toString() || '-',
            sizeInBytes: profile.sizeInBytes ? bytesFormatter(profile.sizeInBytes) : '-',
        };
    });

    const tableColumns = [
        {
            title: t('Date'),
            key: 'Date',
            dataIndex: 'timestamp',
            render: (title, record, index) => {
                return (
                    <Button type="text" onClick={() => showProfileModal(index)}>
                        <Typography.Text underline>{title}</Typography.Text>
                    </Button>
                );
            },
        },
        {
            title: t('Row Count'),
            key: 'Row Count',
            dataIndex: 'rowCount',
        },
        {
            title: t('Column Count'),
            key: 'Column Count',
            dataIndex: 'columnCount',
        },
        {
            title: t('Size'),
            key: 'Size',
            dataIndex: 'sizeInBytes',
        },
    ];

    const selectedProfile = (selectedProfileIndex >= 0 && profiles[selectedProfileIndex]) || undefined;
    const profileModalTitle =
        selectedProfile &&
        `${t('Showing profile from')} ${new Date(selectedProfile?.timestampMillis).toLocaleDateString()} ${t(
            'at',
        )} ${new Date(selectedProfile?.timestampMillis).toLocaleTimeString()}`;

    return (
        <>
            {selectedProfile && (
                <Modal width="100%" footer={null} title={profileModalTitle} visible={showModal} onCancel={onClose}>
                    <TableStats
                        rowCount={selectedProfile.rowCount || -1}
                        columnCount={selectedProfile.columnCount || -1}
                    />
                    <ColumnStats columnStats={selectedProfile.fieldProfiles || []} />
                </Modal>
            )}
            <ChartTable
                scroll={{ y: 400 }}
                style={{ width: '100%', maxHeight: 440 }}
                columns={tableColumns}
                dataSource={tableData}
                pagination={false}
                locale={{
                    emptyText: <Empty description={t('No Profiling Runs')} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                }}
            />
        </>
    );
}
